/**
 * AdminPreferredTermsButton — Round P152 (Jun 2026).
 *
 * Panel for the ~22 preferred stocks: admin enters (or EDGAR-suggests,
 * then confirms) each security's par value + call date. Saved terms
 * land on stock_cache (par_value / preferred_call_date) and power the
 * above-par call-risk chip in the Stock Modal. EDGAR suggestions are
 * pre-fills ONLY — measured accuracy is ~1/3, so nothing goes live
 * without the admin pressing Save.
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, X, ShieldAlert, Wand2, Save, ExternalLink, Ban, UserCog } from 'lucide-react';
import { ExcludedNotFixedRate, markNotFixedRate } from './AdminTagBabyBondsButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GRID = 'grid grid-cols-[80px_1fr_84px_110px_130px_32px_32px_32px_32px] gap-2';

function TermsRow({ row, onSaved, onNotFixedRate, onStockClick }) {
  const [par, setPar] = useState(row.par_value ?? '');
  const [callDate, setCallDate] = useState(
    row.preferred_call_date && row.preferred_call_date !== 'issuer-choice' ? row.preferred_call_date : '',
  );
  const [issuerChoice, setIssuerChoice] = useState(row.preferred_call_date === 'issuer-choice');
  // Oct 2026 — Fixed-to-Floating reset terms (only shown for floaters).
  const [floatBenchmark, setFloatBenchmark] = useState(row.float_benchmark || '');
  const [floatSpread, setFloatSpread] = useState(row.float_spread_pct ?? '');
  const [suggestion, setSuggestion] = useState(null);
  const isFloater = /floating/i.test(row.name || '') || suggestion?.float_spread_pct != null || row.float_spread_pct != null;
  const [busy, setBusy] = useState(null); // 'suggest' | 'save'

  const price = row.price;
  const parNum = par === '' ? null : Number(par);
  const abovePar = parNum && price ? price > parNum : false;
  const callPast = !issuerChoice && callDate && callDate <= new Date().toISOString().slice(0, 10);

  const suggest = async () => {
    setBusy('suggest');
    try {
      const { data } = await axios.post(
        `${API}/admin/preferred-terms/${row.ticker}/suggest`, {}, { withCredentials: true },
      );
      setSuggestion(data);
      if (data.par_value != null && par === '') setPar(String(data.par_value));
      if (data.call_date && !callDate && !issuerChoice) setCallDate(data.call_date);
      if (data.float_benchmark && !floatBenchmark) setFloatBenchmark(data.float_benchmark);
      if (data.float_spread_pct != null && floatSpread === '') setFloatSpread(String(data.float_spread_pct));
      if (data.par_value == null && !data.call_date) {
        toast.info(`${row.ticker}: EDGAR found nothing usable`, {
          description: 'Enter the terms manually from the prospectus or Preferred Stock Channel.',
        });
      }
    } catch (err) {
      toast.error(`Suggest failed for ${row.ticker}`, { description: err?.response?.data?.detail || err?.message });
    } finally {
      setBusy(null);
    }
  };

  const save = async () => {
    setBusy('save');
    try {
      const payloadCall = issuerChoice ? 'issuer-choice' : (callDate || null);
      await axios.post(
        `${API}/admin/preferred-terms/${row.ticker}`,
        {
          par_value: par === '' ? null : Number(par),
          call_date: payloadCall,
          float_benchmark: isFloater ? (floatBenchmark || null) : null,
          float_spread_pct: isFloater && floatSpread !== '' ? Number(floatSpread) : null,
          // Oct 2026 — persist the prospectus sentence(s) the wand matched,
          // but only for the fields whose saved value still matches EDGAR.
          prospectus_quote: suggestion ? {
            call: payloadCall && suggestion.call_date === payloadCall ? suggestion.call_snippet : null,
            par: par !== '' && suggestion.par_value != null && Number(par) === Number(suggestion.par_value) ? suggestion.par_snippet : null,
            float: isFloater && floatSpread !== '' && Number(floatSpread) === Number(suggestion.float_spread_pct) ? suggestion.float_snippet : null,
            doc_url: suggestion.doc_url,
            source: suggestion.source,
          } : undefined,
        },
        { withCredentials: true },
      );
      toast.success(`${row.ticker} terms saved`);
      onSaved(row.ticker, par === '' ? null : Number(par), payloadCall);
    } catch (err) {
      toast.error(`Save failed for ${row.ticker}`, { description: err?.response?.data?.detail || err?.message });
    } finally {
      setBusy(null);
    }
  };

  const notFixedRate = async () => {
    setBusy('nfr');
    try {
      await markNotFixedRate(row.ticker, true);
      toast.success(`${row.ticker} marked not fixed-rate`, { description: 'Call terms cleared; kept out of this panel and fixed-rate tagging.' });
      onNotFixedRate(row);
    } catch (err) {
      toast.error(`Could not flag ${row.ticker}`, { description: err?.response?.data?.detail || err?.message });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="px-3 py-2 border-b border-border/60 last:border-b-0" data-testid={`preferred-terms-row-${row.ticker}`}>
      <div className={`${GRID} items-center text-xs`}>
        {typeof onStockClick === 'function' ? (
          <button
            type="button"
            onClick={() => onStockClick(row.ticker)}
            className="font-bold text-left text-violet-700 dark:text-violet-300 hover:underline"
            title={`Open ${row.ticker} in the Stock Modal`}
            data-testid={`preferred-terms-open-${row.ticker}`}
          >
            {row.ticker}
          </button>
        ) : (
          <span className="font-bold">{row.ticker}</span>
        )}
        <span className="text-muted-foreground truncate" title={row.name}>{row.name}</span>
        <span className="tabular-nums">{price != null ? `$${Number(price).toFixed(2)}` : '—'}</span>
        <input
          type="number" step="0.01" min="0" value={par}
          onChange={(e) => setPar(e.target.value)}
          placeholder="Par $"
          className="h-7 px-2 rounded border border-input bg-background text-xs tabular-nums"
          data-testid={`preferred-terms-par-${row.ticker}`}
        />
        {issuerChoice ? (
          <span
            className="h-7 px-2 inline-flex items-center justify-center rounded border border-sky-400/60 bg-sky-500/10 text-sky-700 dark:text-sky-300 text-[10px] font-semibold uppercase tracking-wide"
            title="No fixed call date — redemption happens on 15–60 business-days notice at the issuer's option (per the prospectus)."
            data-testid={`preferred-terms-call-issuer-${row.ticker}`}
          >
            Issuer&apos;s choice
          </span>
        ) : (
          <input
            type="date" value={callDate}
            onChange={(e) => setCallDate(e.target.value)}
            className="h-7 px-2 rounded border border-input bg-background text-xs"
            data-testid={`preferred-terms-call-${row.ticker}`}
          />
        )}
        <button
          type="button"
          onClick={() => {
            setIssuerChoice((v) => {
              const next = !v;
              if (next) setCallDate('');
              return next;
            });
          }}
          className={`inline-flex items-center justify-center w-7 h-7 rounded ${issuerChoice ? 'bg-sky-500/20 text-sky-700 dark:text-sky-300' : 'text-sky-500 hover:bg-sky-500/10'}`}
          title="Toggle 'At issuer's choice' — use when the prospectus allows redemption at any time on 15–60 days' notice with no fixed call date (e.g. STRD)."
          data-testid={`preferred-terms-issuer-choice-${row.ticker}`}
        >
          <UserCog className="w-3.5 h-3.5" />
        </button>
        <button
          type="button" onClick={suggest} disabled={!!busy}
          className="inline-flex items-center justify-center w-7 h-7 rounded text-violet-500 hover:bg-violet-500/10 disabled:opacity-40"
          title="Suggest from EDGAR prospectus (pre-fill only — verify before saving)"
          data-testid={`preferred-terms-suggest-${row.ticker}`}
        >
          {busy === 'suggest' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
        </button>
        <button
          type="button" onClick={save} disabled={!!busy}
          className="inline-flex items-center justify-center w-7 h-7 rounded text-emerald-600 hover:bg-emerald-500/10 disabled:opacity-40"
          title="Save terms (goes live in the stock modal)"
          data-testid={`preferred-terms-save-${row.ticker}`}
        >
          {busy === 'save' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
        </button>
        <button
          type="button" onClick={notFixedRate} disabled={!!busy}
          className="inline-flex items-center justify-center w-7 h-7 rounded text-sky-500 hover:bg-sky-500/10 disabled:opacity-40"
          title="Not a fixed-rate asset (variable/board-set rate) — clear call terms and keep it out of this panel"
          data-testid={`preferred-terms-not-fixed-${row.ticker}`}
        >
          {busy === 'nfr' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}
        </button>
      </div>
      {isFloater && (
        <div className="flex items-center gap-2 mt-1 pl-[80px] text-[10px]" data-testid={`preferred-terms-float-row-${row.ticker}`}>
          <span className="text-sky-600 dark:text-sky-400 font-semibold uppercase tracking-wide">Fixed→Float reset:</span>
          <select
            value={floatBenchmark}
            onChange={(e) => setFloatBenchmark(e.target.value)}
            className="h-6 px-1.5 rounded border border-input bg-background text-[10px]"
            data-testid={`preferred-terms-float-benchmark-${row.ticker}`}
          >
            <option value="">Benchmark…</option>
            <option value="3M SOFR">3-Month SOFR</option>
            <option value="3M SOFR + 0.26161% CSA">3-Month SOFR + 0.26161% CSA (ex-LIBOR)</option>
            <option value="3M LIBOR">3-Month LIBOR (legacy)</option>
            <option value="5Y Treasury">5-Year Treasury</option>
            <option value="10Y Treasury">10-Year Treasury</option>
          </select>
          <span>+</span>
          <input
            type="number" step="0.001" min="0" max="20" value={floatSpread}
            onChange={(e) => setFloatSpread(e.target.value)}
            placeholder="spread %"
            className="h-6 w-24 px-1.5 rounded border border-input bg-background text-[10px] tabular-nums"
            data-testid={`preferred-terms-float-spread-${row.ticker}`}
          />
          <span className="text-muted-foreground">% — from the prospectus ("…plus a spread of X.XXX%"). Saved with the Save button.</span>
        </div>
      )}
      <div className="flex items-center gap-2 mt-1 pl-[80px] text-[10px]">
        {abovePar && callPast && (
          <span className="text-red-500 font-semibold">⚠ above par + past call date</span>
        )}
        {abovePar && callDate && !callPast && !issuerChoice && (
          <span className="text-amber-500">above par · callable {callDate}</span>
        )}
        {issuerChoice && (
          <span className="text-sky-600 dark:text-sky-400">
            {abovePar ? '⚠ above par · ' : ''}callable any time &mdash; 15&ndash;60 business-days notice at the issuer&apos;s choice
          </span>
        )}
        {suggestion && (
          <span className="text-muted-foreground">
            EDGAR: par {suggestion.par_value != null ? `$${suggestion.par_value}` : '—'} · call {suggestion.call_date || '—'}
            {suggestion.float_spread_pct != null ? ` · float ${suggestion.float_benchmark || 'benchmark'} + ${suggestion.float_spread_pct}%` : ''}
            {suggestion.source ? ` (${suggestion.source})` : ''}
            {suggestion.note ? ` — ${suggestion.note}` : ''}
            {suggestion.doc_url && (
              <a href={suggestion.doc_url} target="_blank" rel="noreferrer" className="inline-flex ml-1 align-middle text-primary">
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </span>
        )}
        {row.preferred_terms_source && !suggestion && (
          <span className="text-muted-foreground">{row.preferred_terms_source}</span>
        )}
        {row.prospectus_quote && !suggestion && (
          <span className="text-amber-700/80 dark:text-amber-400/70" title={row.prospectus_quote.call || row.prospectus_quote.par || ''} data-testid={`preferred-terms-quote-saved-${row.ticker}`}>
            · prospectus quote saved{row.prospectus_quote.source ? ` (${row.prospectus_quote.source})` : ''}
          </span>
        )}
      </div>
      {suggestion && (suggestion.call_snippet || suggestion.par_snippet || suggestion.float_snippet) && (
        <div className="mt-1 pl-[80px] space-y-1" data-testid={`preferred-terms-snippets-${row.ticker}`}>
          {[['Optional Redemption', suggestion.call_snippet, 'call'], ['Par / liquidation preference', suggestion.par_snippet, 'par'], ['Floating-rate reset', suggestion.float_snippet, 'float']]
            .filter(([, snip]) => !!snip)
            .map(([label, snip, key]) => (
              <blockquote
                key={key}
                className="text-[10px] leading-snug border-l-2 border-amber-400/70 pl-2 pr-1 py-0.5 bg-amber-50/60 dark:bg-amber-950/20 text-foreground/85 italic"
                data-testid={`preferred-terms-snippet-${key}-${row.ticker}`}
              >
                <span className="not-italic font-semibold text-amber-700 dark:text-amber-400 mr-1">{label}:</span>
                &ldquo;{snip}&rdquo;
                {suggestion.doc_url && (
                  <a href={suggestion.doc_url} target="_blank" rel="noreferrer" className="not-italic ml-1 text-primary underline decoration-dotted" title="Open the full prospectus on EDGAR">source ↗</a>
                )}
              </blockquote>
            ))}
        </div>
      )}
    </div>
  );
}

export default function AdminPreferredTermsButton({ onStockClick }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [excluded, setExcluded] = useState([]);

  const openModal = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/preferred-terms`, { withCredentials: true });
      setRows(data.rows || []);
      setExcluded(data.excluded_not_fixed_rate || []);
    } catch (err) {
      toast.error('Could not load preferred terms', { description: err?.response?.data?.detail || err?.message });
    } finally {
      setLoading(false);
    }
  };

  const onNotFixedRate = (row) => {
    setRows((prev) => prev.filter((r) => r.ticker !== row.ticker));
    setExcluded((prev) => [...prev, { ticker: row.ticker, name: row.name, reason: 'admin', admin_flag: true }]);
  };

  const undoNotFixedRate = async (ticker) => {
    try {
      await markNotFixedRate(ticker, false);
      setExcluded((prev) => prev.filter((r) => r.ticker !== ticker));
      toast.success(`${ticker} flag lifted — re-open to reload`);
    } catch (err) {
      toast.error(`Could not undo ${ticker}`, { description: err?.response?.data?.detail || err?.message });
    }
  };

  const onSaved = (ticker, par, callDate) => {
    setRows((prev) => prev.map((r) => (r.ticker === ticker
      ? { ...r, par_value: par, preferred_call_date: callDate }
      : r)));
  };

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-violet-600/10 text-violet-600 dark:text-violet-400 border border-violet-500/30 hover:bg-violet-600/20 transition-colors"
        data-testid="admin-preferred-terms-btn"
      >
        <ShieldAlert className="w-3.5 h-3.5" />
        Preferred Terms
      </button>
      {open && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/60 p-4" onClick={() => setOpen(false)}>
          <div
            className="w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded-lg border border-border bg-card shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="admin-preferred-terms-modal"
          >
            <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 bg-card border-b border-border">
              <div>
                <h3 className="text-sm font-bold flex items-center gap-2">
                  <ShieldAlert className="w-4 h-4 text-violet-500" /> Preferred Terms — par value &amp; call dates
                </h3>
                <p className="text-[10.5px] text-muted-foreground mt-0.5">
                  Wand = EDGAR suggestion (pre-fill only, ~1/3 accurate — verify against the prospectus). Save publishes the terms and lights the above-par call-risk chip in the Stock Modal.
                </p>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="p-1 rounded hover:bg-muted" data-testid="preferred-terms-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground text-xs">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading preferreds…
              </div>
            ) : (
              <div>
                <div className={`${GRID} px-3 py-2 bg-muted/40 text-[10px] font-bold uppercase tracking-wide text-muted-foreground`}>
                  <div>Ticker</div><div>Name</div><div>Price</div><div>Par $</div><div>Call date</div><div /><div /><div /><div />
                </div>
                {rows.map((r) => <TermsRow key={r.ticker} row={r} onSaved={onSaved} onNotFixedRate={onNotFixedRate} onStockClick={onStockClick} />)}
                {rows.length === 0 && (
                  <div className="px-4 py-8 text-center text-xs text-muted-foreground">No preferred stocks classified yet.</div>
                )}
                <div className="p-3">
                  <ExcludedNotFixedRate rows={excluded} onUndo={undoNotFixedRate} testId="preferred-terms-excluded" />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
